import { Dispatch, SetStateAction } from "react";
import { Lesson } from "@/types/tutor";

export type LessonFilters = {
  filterType: string;
  sortOrder: string;
  searchValue: string;
  // Optional progress filter for student dashboard
  statusFilter?: "all" | "mastered" | "in_progress" | "not_started";
};

export type FilterUpdate = Partial<LessonFilters>;

export const filterAndSortLessons = <T extends Lesson & { status?: string }>(
  lessons: T[],
  filters: LessonFilters,
): T[] => {
  let filtered = [...lessons];

  if (filters.searchValue.trim()) {
    filtered = filtered.filter((lesson) =>
      lesson.name.toLowerCase().includes(filters.searchValue.toLowerCase()),
    );
  }

  // Apply optional progress filter when provided (student view)
  if (filters.statusFilter && filters.statusFilter !== "all") {
    const desired = filters.statusFilter;
    filtered = filtered.filter((lesson) => {
      const status = (lesson as any).status as string | undefined;
      if (!status) return false;
      return status === desired;
    });
  }

  filtered.sort((a, b) => {
    let comparison = 0;

    if (filters.filterType === "lesson_name") {
      comparison = a.name.localeCompare(b.name);
    } else if (filters.filterType === "created_at") {
      const dateA = new Date(a.created_at).getTime();
      const dateB = new Date(b.created_at).getTime();
      comparison = dateA - dateB;
    } else if (filters.filterType === "mastery") {
      // Map status to an ordinal for sorting
      const order = (s?: string) =>
        s === "mastered" ? 2 : s === "in_progress" ? 1 : 0;
      const statusA = (a as any).status as string | undefined;
      const statusB = (b as any).status as string | undefined;
      const ordDiff = order(statusA) - order(statusB);
      if (ordDiff !== 0) {
        comparison = ordDiff;
      } else {
        // Tie-breaker: progress ratio if available
        const ratio = (l: any) => {
          const gm = Number(l.goalsMet ?? 0);
          const tg = Number(l.totalGoals ?? 0);
          return tg > 0 ? gm / tg : 0;
        };
        const rdiff = ratio(a as any) - ratio(b as any);
        if (rdiff !== 0) {
          comparison = rdiff;
        } else {
          // Final tie-breaker: name
          comparison = a.name.localeCompare(b.name);
        }
      }
    } else if (filters.filterType === "status_then_date") {
      // Primary: not_started (0) < in_progress (1) < mastered (2)
      const order = (s?: string) =>
        s === "not_started" ? 0 : s === "in_progress" ? 1 : s === "mastered" ? 2 : 3;
      const statusA = (a as any).status as string | undefined;
      const statusB = (b as any).status as string | undefined;
      const ordDiff = order(statusA) - order(statusB);
      if (ordDiff !== 0) {
        comparison = ordDiff;
      } else {
        // Secondary: most recently assigned first (descending date)
        const dateA = new Date((a as any).assignedAt || a.created_at).getTime();
        const dateB = new Date((b as any).assignedAt || b.created_at).getTime();
        comparison = dateB - dateA;
      }
    }

    return filters.sortOrder === "asc" ? comparison : -comparison;
  });

  return filtered;
};

export const createFilterUpdateHandler = (
  setFilters: Dispatch<SetStateAction<LessonFilters>>,
) => {
  return (update: FilterUpdate) => {
    setFilters((prev) => ({ ...prev, ...update }));
  };
};
